'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ServiceCardProps {
  service: {
    id: string;
    title: string;
    description: string;
    price: number;
    priceLabel: string;
    image: string;
    alt: string;
    features: string[];
    popular?: boolean;
    verified: boolean;
    rating: number;
    reviewCount: number;
    provider: string;
    ctaText: string;
    ctaLink: string;
  };
}

export default function ServiceCard({ service }: ServiceCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="bg-card rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <div className="relative h-56 overflow-hidden">
        <AppImage
          src={service.image}
          alt={service.alt}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            isHovered ? 'scale-110' : 'scale-100'
          }`}
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {service.verified && (
            <div className="bg-trust-green text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg">
              <Icon name="ShieldCheckIcon" size={14} variant="solid" />
              <span>Verified</span>
            </div>
          )}
          {service.popular && (
            <div className="bg-gradient-to-r from-coral to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
              POPULAR
            </div>
          )}
        </div>

        {/* Provider */}
        <div className="absolute bottom-4 left-4">
          <div className="bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg">
            <p className="text-xs font-semibold text-gray-900">{service.provider}</p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6">
        {/* Title & Rating */}
        <div className="mb-3">
          <h3 className="font-heading font-bold text-xl text-foreground mb-2 line-clamp-2">
            {service.title}
          </h3>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Icon name="StarIcon" size={16} className="text-accent" variant="solid" />
              <span className="text-sm font-bold text-foreground">{service.rating}</span>
            </div>
            <span className="text-sm text-muted-foreground">({service.reviewCount.toLocaleString()} reviews)</span>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {service.description}
        </p>

        {/* Features */}
        <div className="space-y-2 mb-5">
          {service.features.slice(0, 4).map((feature, index) => (
            <div key={index} className="flex items-start gap-2">
              <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" variant="solid" />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-5 border-t border-border">
          <div>
            {service.price > 0 ? (
              <>
                <p className="text-xs text-muted-foreground mb-1">Starting from</p>
                <p className="text-2xl font-black text-primary">
                  £{service.price.toFixed(2)}
                </p>
              </>
            ) : (
              <p className="text-2xl font-black text-trust-green">
                Free Quote
              </p>
            )}
          </div>

          <Link
            href={service.ctaLink}
            className="px-6 py-3 bg-gradient-to-r from-coral to-orange-500 text-white text-sm font-bold rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2 group-hover:scale-105"
          >
            <span>{service.ctaText}</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}